/**
 * Work (Smlouva o dílo) Contract Configuration
 *
 * Declarative configuration for "Smlouva o dílo" using GenericContractForm.
 * Keeps fields aligned with backend WorkContractData schema.
 */

import { ContractFormConfig, FieldType } from '../../types/contractForm.types';

export const workContractConfig: ContractFormConfig = {
  type: 'work',
  subtype: undefined,

  metadata: {
    title: 'Smlouva o dílo',
    description: 'Konfigurace pro vytvoření smlouvy o dílo',
    version: '1.0.0',
    lastModified: new Date().toISOString(),
  },

  steps: [
    // Step 0: Contractor (Zhotovitel)
    {
      title: 'Kdo dílo provede?',
      subtitle: 'Údaje o zhotoviteli',
      description: 'Identifikace zhotovitele (fyzická nebo právnická osoba)',
      icon: 'Build',
      color: 'primary',
      fields: [
        {
          name: 'contractor_name',
          label: 'Jméno a příjmení / Název firmy',
          type: FieldType.TEXT,
          validation: [ { type: 'required', message: 'Jméno zhotovitele je povinné' } ],
          gridProps: { xs: 12 },
          placeholder: 'např. Jan Novák nebo Stavby CZ s.r.o.',
        },
        {
          name: 'contractor_address',
          label: 'Adresa',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [ { type: 'required', message: 'Adresa zhotovitele je povinná' } ],
          gridProps: { xs: 12 },
          placeholder: 'Ulice a číslo, PSČ Město',
        },
        {
          name: 'contractor_id',
          label: 'Rodné číslo / IČO',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Rodné číslo nebo IČO je povinné' },
            { type: 'pattern', value: /^(\d{6}\/?\d{4}|\d{8})$/, message: 'Neplatný formát RČ/IČO' },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '123456/7890 nebo 12345678',
        },
        {
          name: 'contractor_email',
          label: 'E‑mail (volitelné)',
          type: FieldType.EMAIL,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'jmeno@email.cz',
        },
        {
          name: 'contractor_business_id',
          label: 'IČO (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: '12345678',
        },
        {
          name: 'contractor_tax_id',
          label: 'DIČ (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'CZ12345678',
        },
      ],
    },

    // Step 1: Client (Objednavatel)
    {
      title: 'Kdo si dílo objednává?',
      subtitle: 'Údaje o objednavateli',
      description: 'Identifikace objednavatele',
      icon: 'Person',
      color: 'secondary',
      fields: [
        {
          name: 'client_name',
          label: 'Jméno a příjmení / Název firmy',
          type: FieldType.TEXT,
          validation: [ { type: 'required', message: 'Jméno objednavatele je povinné' } ],
          gridProps: { xs: 12 },
          placeholder: 'např. Petra Nováková nebo Domy s.r.o.',
        },
        {
          name: 'client_address',
          label: 'Adresa',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [ { type: 'required', message: 'Adresa objednavatele je povinná' } ],
          gridProps: { xs: 12 },
          placeholder: 'Ulice a číslo, PSČ Město',
        },
        {
          name: 'client_id',
          label: 'Rodné číslo / IČO',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Rodné číslo nebo IČO je povinné' },
            { type: 'pattern', value: /^(\d{6}\/?\d{4}|\d{8})$/, message: 'Neplatný formát RČ/IČO' },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '123456/7890 nebo 12345678',
        },
        {
          name: 'client_email',
          label: 'E‑mail (volitelné)',
          type: FieldType.EMAIL,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'jmeno@email.cz',
        },
        {
          name: 'client_business_id',
          label: 'IČO (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: '87654321',
        },
      ],
    },

    // Step 2: Work Details
    {
      title: 'Co je předmětem díla?',
      subtitle: 'Specifikace a místo plnění',
      description: 'Popis díla, místo provedení a specifikace',
      icon: 'Description',
      color: 'warning',
      fields: [
        {
          name: 'work_description',
          label: 'Popis díla',
          type: FieldType.MULTILINE,
          rows: 3,
          validation: [ { type: 'required', message: 'Popis díla je povinný' } ],
          gridProps: { xs: 12 },
          placeholder: 'Detailní popis prací / výsledku díla',
        },
        {
          name: 'work_location',
          label: 'Místo provedení (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12 },
          placeholder: 'např. Praha 4, U Lesa 12',
        },
        {
          name: 'materials_provider',
          label: 'Kdo dodá materiál',
          type: FieldType.SELECT,
          options: [
            { value: 'Zhotovitel', label: 'Zhotovitel' },
            { value: 'Objednavatel', label: 'Objednavatel' },
          ],
          validation: [ { type: 'required', message: 'Zvolte, kdo dodá materiál' } ],
          gridProps: { xs: 12, md: 6 },
        },
        {
          name: 'work_specifications',
          label: 'Technická specifikace (volitelné)',
          type: FieldType.MULTILINE,
          rows: 3,
          gridProps: { xs: 12 },
          placeholder: 'Normy, materiály, postupy…',
        },
      ],
    },

    // Step 3: Price & Payment
    {
      title: 'Cena a platby',
      subtitle: 'Celková cena, způsob a harmonogram plateb',
      description: 'Nastavení ceny díla a platebních podmínek',
      icon: 'Payments',
      color: 'success',
      fields: [
        {
          name: 'total_price',
          label: 'Cena díla (Kč)',
          type: FieldType.NUMBER,
          validation: [ { type: 'required', message: 'Cena díla je povinná' }, { type: 'min', value: 0, message: 'Cena musí být nezáporná' } ],
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. 150000',
        },
        {
          name: 'payment_method',
          label: 'Způsob platby',
          type: FieldType.SELECT,
          options: [
            { value: 'Hotově', label: 'Hotově' },
            { value: 'Bankovním převodem', label: 'Bankovním převodem' },
          ],
          validation: [ { type: 'required', message: 'Způsob platby je povinný' } ],
          gridProps: { xs: 12, md: 6 },
        },
        {
          name: 'payment_schedule',
          label: 'Platební kalendář (volitelné)',
          type: FieldType.MULTILINE,
          rows: 3,
          gridProps: { xs: 12 },
          placeholder: 'Zálohy, milníky, termíny…',
        },
      ],
    },

    // Step 4: Termíny, záruky a sankce
    {
      title: 'Termíny a záruky',
      subtitle: 'Dokončení, kontrola, záruka a sankce',
      description: 'Nastavení termínů a záruk',
      icon: 'Schedule',
      color: 'info',
      fields: [
        {
          name: 'completion_date',
          label: 'Termín dokončení',
          type: FieldType.DATE,
          validation: [ { type: 'required', message: 'Termín dokončení je povinný' } ],
          gridProps: { xs: 12, md: 6 },
        },
        {
          name: 'inspection_period',
          label: 'Lhůta pro kontrolu (dny)',
          type: FieldType.NUMBER,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. 15',
        },
        {
          name: 'warranty_period',
          label: 'Záruční doba (měsíce)',
          type: FieldType.NUMBER,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. 24',
        },
        {
          name: 'penalty_clause',
          label: 'Smluvní pokuty (volitelné)',
          type: FieldType.MULTILINE,
          rows: 3,
          gridProps: { xs: 12 },
          placeholder: 'Výše a podmínky smluvních pokut…',
        },
        {
          name: 'insurance_required',
          label: 'Požadovat pojištění odpovědnosti',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12 },
        },
        {
          name: 'special_conditions',
          label: 'Zvláštní podmínky (volitelné)',
          type: FieldType.MULTILINE,
          rows: 3,
          gridProps: { xs: 12 },
        },
      ],
    },
  ],
};

export default workContractConfig;

